import classNames from 'classnames';

import {
  addMonths,
  endOfMonth,
  format,
  getDate,
  getDay,
  getTime,
  setDate,
  startOfMonth,
  subMonths,
} from 'date-fns';
import { Range } from 'immutable';
import chunk from 'lodash/chunk';
import type React from 'react';

type DayProps = {
  d: number;
  i: number;
  w: number;
  onClick: () => void;
};

const Day = (props: DayProps) => {
  const prevMonth = props.w === 0 && props.i > 7;
  const nextMonth = props.w >= 4 && props.i <= 14;
  const currentDay = !prevMonth && !nextMonth && props.i === props.d;

  return (
    <td
      className={classNames(
        'ipf-date-time-picker__date-time-table-border',
        currentDay &&
          'ipf-date-time-picker__date-time-table-border--current-day',
        (prevMonth || nextMonth) &&
          'ipf-date-time-picker__date-time-table-border--month',
      )}
      onClick={props.onClick}
    >
      {props.i}
    </td>
  );
};

type Props = {
  datetime: number;
  onChange: (value: number) => void;
};

export const Calendar = (props: Props) => {
  const selectDate = (i: number, w: number) => () => {
    const { datetime: dt, onChange } = props;
    const prevMonth = w === 0 && i > 7;
    const nextMonth = w >= 4 && i <= 14;

    if (prevMonth) {
      return onChange(getTime(setDate(subMonths(new Date(dt), 1), i)));
    }
    if (nextMonth) {
      return onChange(getTime(setDate(addMonths(new Date(dt), 1), i)));
    }
    return onChange(getTime(setDate(new Date(dt), i)));
  };

  const prevMonth = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const { datetime, onChange } = props;
    onChange(getTime(subMonths(new Date(datetime), 1)));
  };

  const nextMonth = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const { datetime, onChange } = props;
    onChange(getTime(addMonths(new Date(datetime), 1)));
  };

  const { datetime: dt } = props;
  const newDate = new Date(dt);
  const d = getDate(dt);
  const d1 = getDate(endOfMonth(subMonths(newDate, 1))); // last day of previous month
  const d2 = getDay(startOfMonth(newDate)); // number of first day in first week
  const d3 = getDate(endOfMonth(newDate)); // last day of current month

  const days = ([] as number[]).concat(
    Range(d1 - d2 + 1, d1 + 1).toArray(),
    Range(1, d3 + 1).toArray(),
    Range(1, 42 - d3 - d2 + 1).toArray(),
  );

  const weeks = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className='ipf-date-time-picker__date-time-tab'>
      <div className='ipf-date-time-picker__date-time-toolbar'>
        <button
          className='prev-month ipf-date-time-picker__date-time-toolbar-button float-left'
          onClick={prevMonth}
          type='button'
        >
          <i className='fa fa-angle-left ipf-date-time-picker__date-time-toolbar-button-icon' />
        </button>
        <span className='ipf-date-time-picker__date-time-current-date'>
          {format(newDate, 'MMMM yyyy')}
        </span>
        <button
          className='next-month ipf-date-time-picker__date-time-toolbar-button float-right'
          onClick={nextMonth}
          type='button'
        >
          <i className='fa fa-angle-right ipf-date-time-picker__date-time-toolbar-button-icon' />
        </button>
      </div>

      <table className='ipf-date-time-picker__date-time-table'>
        <thead>
          <tr>
            {weeks.map((w) => (
              <td
                key={w}
                className='ipf-date-time-picker__date-time-table-border
                  ipf-date-time-picker__date-time-table-border-thead'
              >
                {w}
              </td>
            ))}
          </tr>
        </thead>

        <tbody>
          {chunk(days, 7).map((row, w) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: chunk number is the stable identifier there
            <tr key={w}>
              {row.map((i) => (
                <Day key={i} i={i} d={d} w={w} onClick={selectDate(i, w)} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
